import { callDelete, callGet, callPost, callPostWithFormData, delay, } from "./index";
import { CognitoAuth } from "./CognitoAuth";
import { UserDocument } from "../models/UserDocument";
import { Folder } from "../models/Folder";
import { UserDocumentVersion } from "../models/UserDocumentVersion";
import { isLead } from "../constants"

type UploadProgressEvent = {
  loaded: number;
  total?: number;
  progress?: number;
  bytes: number;
  rate?: number;
  estimated?: number;
  upload?: boolean;
  download?: boolean;
  event?: any;
  lengthComputable: boolean;
};

export class DocumentsAPI {
  async getFolders(rethrowError = false): Promise<Folder[] | undefined> {
    try {
      const path = `${Folder.path}`;
      const response = await callGet(path, rethrowError);
      return response?.data?.map((json: any) => Folder.fromJSON(json));
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getFolders error");
    }
  }

  async getDocuments(
    rethrowError = false
  ): Promise<UserDocument[] | undefined> {
    try {
      const path = `${UserDocument.path}`;
      // path += `?language_id=${language_id}&terms=${terms}`;
      const response = await callGet(path, rethrowError);
      return response?.data?.map((json: any) => UserDocument.fromJSON(json));
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getDocuments error");
    }
  }

  async getDocument(id: number, rethrowError = false): Promise<any> {
    try {
      const path = `${UserDocument.path}/${id}`;
      const response = await callGet(path, rethrowError);
      return response?.data;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getDocument error");
    }
  }

  async getDocumentVersions(
    document: UserDocument,
    rethrowError = false
  ): Promise<UserDocumentVersion[] | undefined> {
    try {
      const path = `${UserDocument.path}/${document.id}/${UserDocumentVersion.path}`;
      // path += `?language_id=${language_id}&terms=${terms}`;
      const response = await callGet(path, rethrowError);
      return response?.data?.map((json: any) =>
        UserDocumentVersion.fromJSON(json)
      );
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getDocumentVersions error");
    }
  }

  async revertToDocumentVersion(
    document: UserDocument,
    versionId: number,
    rethrowError = false
  ): Promise<UserDocumentVersion[] | undefined> {
    try {
      const path = `${UserDocument.path}/${document.id}/${UserDocumentVersion.path}`;
      const response = await callPost(
        path,
        {
          version: versionId,
        },
        rethrowError
      );
      return response?.data?.map((json: any) =>
        UserDocumentVersion.fromJSON(json)
      );
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getDocumentVersions error");
    }
  }

  async fetchUrl(
    id: number,
    rethrowError = false
  ): Promise<string | undefined> {
    try {
      const fullPath = `${UserDocument.path}/${id}/url`;
      const response = await callGet(fullPath, rethrowError);
      return response?.data;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "fetchUrl error");
    }
  }

  async saveDocument(
    title?: string,
    documentId?: number,
    documentTypeId?: number,
    file?: File,
    notes?: string,
    folderId?: number,
    progressCallBack?: (percent: number) => void,
    artificialDelay = false
  ): Promise<UserDocument | null> {
    const isNew = !documentId;
    if (!title) {
      throw Error("title is required");
    }
    if (!documentTypeId) {
      throw Error("documentTypeId is required");
    }
    if (isNew && !file) {
      throw Error("file is required");
    }
    const formData = new FormData();
    formData.append("title", title);
    formData.append("document_type_id", documentTypeId.toString());
    if (notes) {
      formData.append("notes", notes);
    }
    if (file) {
      formData.append("user_document", file, file.name);
    }
    if (folderId) {
      formData.append("folder_id", folderId.toString());
    }
    const onUploadProgress = (progressEvent?: UploadProgressEvent) => {
      const progress = progressEvent ? Math.round(
        (progressEvent.loaded * 100) / (progressEvent.total || 100)
      ) : 0;
      progressCallBack?.(progress);
    };
    try {
      let response;
      if (artificialDelay) {
        await delay(200);
        progressCallBack?.(10);
        await delay(200);
        progressCallBack?.(20);
        await delay(200);
        progressCallBack?.(30);
        await delay(200);
        progressCallBack?.(40);
      }

      if (isNew) {
        // create
        response = await callPostWithFormData(UserDocument.path, formData, true, {
          onUploadProgress,
        });
      } else {
        // update
        formData.append("_method", "PUT");
        // ☝️laravel work-around for php bug
        //  https://github.com/laravel/framework/issues/13457#issuecomment-313949989
        response = await callPostWithFormData(
          `${UserDocument.path}/${documentId}`,
          formData,
          true,
          {
            onUploadProgress,
          }
        );
      }

      return UserDocument.fromJSON({
        title,
        document_id: documentId,
        document_type_id: documentTypeId,
        file,
        notes,
        folder_id: folderId,
        created_at: new Date().toISOString(),
        version: {
          ...((response as any)?.data.message),
          ...response?.data,
          created_at: new Date().toISOString(),
        },
        ...((response as any)?.data.message),
        ...response?.data,
      });
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "saveDocument error");
      return null
    }
  }

  async deleteDocument(
    document: UserDocument,
    rethrowError = false
  ): Promise<boolean> {
    try {
      const path = `${UserDocument.path}/${document.id}`;
      await callDelete(path, rethrowError);
      return true;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "deleteDocument error");
      return false;
    }
  }
}
